import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import { Badge, Carousel, Column, Container, Row } from '@pella/sash-framework';
import { CTA } from '../../';
import { isValidArray } from '../../utilities/validations';
import { contentstackClient } from '../../../utils/contentstack-client';
import { GET_ARTICLES_BY_TAXONOMY } from '../../../enums/contentstack-graphql';

import CardRenderer from '../card-types/card-renderer';

import styles from './index.module.scss';

const TipsAndAdvice = ({ data, dynamic, className = '', parent, hideTitle, ...other }) => {
	const filters = isValidArray(dynamic?.filter_tags) ? dynamic.filter_tags.map((filter) => ({ taxonomies: { filter_by: { term_in: filter?.split(', ') } } })) : [];
	const { data: articleData } = useQuery(GET_ARTICLES_BY_TAXONOMY, { client: contentstackClient, variables: { filters, limit: 5 }, skip: !isValidArray(dynamic?.filter_tags) });
	const combinedLength = (articleData?.all_articles?.items.length || 0) + dynamic?.manual_entries?.length;
	const isMobile = useMediaQuery({ maxWidth: 767 });

	if (!isValidArray(articleData?.all_articles?.items) && !isValidArray(dynamic?.manual_entries)) return null;

	const renderCarouselGradients = (color) => {
		switch (color) {
			case '#FFFFFF':
				return 'before:from-white after:from-white';
			case '#F9F9F9':
			case '#F8F9FA':
				return 'before:from-neutral-25 after:from-neutral-25';
			default:
				return 'before:from-neutral-50 after:from-neutral-50';
		}
	};

	return (
		<div
			id={data.anchor}
			className={`py-8 ${styles.tipsAdvice} ${className}`}
			style={data.background_hex_value ? { backgroundColor: data.background_hex_value } : null}
			data-testid='tips-advice'
			{...other}
		>
			{!hideTitle && (
				<Container className='mb-3 xl:mb-5'>
					<Row align='center'>
						<Column
							xs={12}
							md='fluid'
						>
							{data.heading && (
								<h2
									{...(data.$?.heading ?? {})}
									className={`text-h3 m-0 ${combinedLength < 3 && !isValidArray(dynamic.ctas) ? 'text-center' : ''}`}
								>
									{data.heading}
								</h2>
							)}

							{data.subheading && (
								<div className='text-subtitle mt-2 xl:mt-4'>
									<p
										{...(data.$?.subheading ?? {})}
										className='max-w-[80ch]'
									>
										{data.subheading}
									</p>
								</div>
							)}
						</Column>

						{!isMobile && isValidArray(dynamic.ctas) && (
							<Column
								xs={12}
								md='auto'
								className='mt-2 md:mt-0'
							>
								<CTA
									{...(dynamic.$?.ctas__0 ?? {})}
									data={dynamic.ctas[0]}
									className='!w-fit'
								/>
							</Column>
						)}
					</Row>
				</Container>
			)}

			<Container noPadding>
				<Carousel
					slidesToShow={{ xs: 1, sm: 2, lg: 3 }}
					slidePadding={16}
					className={`tipsAdviceSlider pb-5 pt-2.5 ${renderCarouselGradients(data.background_hex_value)}`}
					dotsClassName='!mt-1 xl:!mt-3'
					showSlideHints
					showArrows
					showDots
				>
					{isValidArray(articleData?.all_articles?.items) &&
						articleData.all_articles.items.map((item, index) => (
							<div
								className={styles.tipsAdvice_Slide}
								key={index}
							>
								<CardRenderer
									data={{
										card_type: 'standard',
										variant: 'standard-padded-content',
										image: [{ image: item.seo?.thumbnailConnection?.edges?.[0]?.node }],
										ctas: [{ link: { href: item.url }, aria: item.top_content?.heading }],
										heading: item.top_content?.heading,
										description: item.top_content?.subheading || item.seo?.description,
										seo_tag: item.top_content?.heading,
									}}
									showCtas={false}
								>
									{item.taxonomies?.find((tag) => tag.term_uid === 'featured_article') && (
										<Badge
											color='primary'
											className='absolute left-0 top-0'
										>
											Featured
										</Badge>
									)}
								</CardRenderer>
							</div>
						))}

					{isValidArray(dynamic?.manual_entries) &&
						dynamic?.manual_entries?.map((item, index) => (
							<div
								className={styles.tipsAdvice_Slide}
								key={index}
							>
								<CardRenderer
									data={{
										card_type: 'standard',
										variant: 'standard-padded-content',
										image: [{ image: item.seo?.thumbnail }],
										ctas: [{ link: { href: item.url }, aria: item.top_content?.heading }],
										heading: item.top_content?.heading,
										description: item.top_content?.subheading || item.seo?.description,
										seo_tag: item.top_content?.heading,
									}}
									showCtas={false}
								>
									{item.taxonomies?.find((tag) => tag.term_uid === 'featured_article') && (
										<Badge
											color='primary'
											className='absolute left-0 top-0'
										>
											Featured
										</Badge>
									)}
								</CardRenderer>
							</div>
						))}
				</Carousel>
			</Container>

			{isMobile && isValidArray(dynamic.ctas) && (
				<Container className='mt-8 text-center'>
					<CTA
						{...(dynamic.$?.ctas__0 ?? {})}
						data={dynamic.ctas[0]}
					/>
				</Container>
			)}
		</div>
	);
};

TipsAndAdvice.propTypes = {
	data: PropTypes.object,
	dynamic: PropTypes.object,
	className: PropTypes.string,
	parent: PropTypes.string,
	hideTitle: PropTypes.bool,
};

export default TipsAndAdvice;
